<template>
  <user-account-section class="post-review-main">
    <template #header>
      <translate>Add your review</translate>
    </template>
    <template #default="{ setBackButtonVisibility }">
      <post-review-main
        v-if="campsiteData"
        :token="token"
        :campsite="campsiteData"
        :initial-booking="initialBooking"
        :initial-error="initialError"
        :set-back-button-visibility="setBackButtonVisibility"
      />
    </template>
  </user-account-section>
</template>

<script setup lang="ts">
import { AjaxCampsiteDetailsRepository } from '../../../../apps/campsite-details/AjaxCampsiteDetailsRepository'
import { CampsiteDetails } from '~/apps/campsite-details/types'
import { computed } from 'vue'
import { GuestBookingRepositoryFetch } from '~/apps/post-review/GuestBookingRepositoryFetch'

definePageMeta({
  layout: 'user-account',
  blockVersionUpdate: true, // Prevent version updates while user is writing review
})

useHead({
  meta: [
    {
      hid: 'robots',
      name: 'robots',
      content: 'noindex',
    },
  ],
})

const token = computed(() => useRoute().query.verify || '')

const asyncKey = `post-review-init-${token.value}`
const { data: initData } = await useAsyncData(asyncKey, async () => {
  try {
    const route = useRoute()
    const lang = (route.params.lang as string) || 'en-gb'
    const campsiteRepo = new AjaxCampsiteDetailsRepository({
      defaults: {
        defaultAdultsCount: useRuntimeConfig().public.defaultAdultsCount,
      },
    })
    const guestRepo = new GuestBookingRepositoryFetch()

    const [campsite, guest] = await Promise.all([
      campsiteRepo.get(route.params.campsiteSlug as string, lang),
      token.value
        ? guestRepo.getByToken(token.value as string, lang)
        : Promise.resolve({ booking: null, error: 'token_required' }),
    ])

    return {
      campsite,
      booking: (guest as any)?.booking || null,
      error: (guest as any)?.booking
        ? null
        : (guest as any)?.error || 'invalid_token',
    }
  } catch (e) {
    console.error(e)
    return { campsite: null, booking: null, error: 'unknown' }
  }
})

const campsiteData = computed<CampsiteDetails | null>(
  () => initData.value?.campsite || null,
)
const initialBooking = computed(() => initData.value?.booking || null)
const initialError = computed(() => initData.value?.error || null)
</script>
